import * as d3 from "d3";
import { calcBoundedLimit } from "./utils";

export default function scatterPlotChart(options) {
  let data;
  let dataSeries = [];
  let seriesLabel;
  let boundedWidth;
  let x, y, color, line;
  let show = false;
  let ecartMeilleurRepartition = 0;
  let useMultipleScales = false; 

  const sortLegendByLastValue = () => {
    const lastValues = dataSeries.map(serie => serie[serie.length - 1].y);
    const sortedIndex = Array.from(Array(dataSeries.length).keys()).sort((a, b) => lastValues[b] - lastValues[a]);
    seriesLabel = sortedIndex.map(index => seriesLabel[index]);
    dataSeries = sortedIndex.map(index => dataSeries[index]);
  };

  const scales = (colorRange, offsetLegend, axisY, axisX, boundedHeight) => {
    data = data.reverse();
    seriesLabel = data.map((d) => d.label);

    dataSeries = data.map((d) => d.serie);

    sortLegendByLastValue(); // Tri de la légende en fonction du dernier nombre

    x = d3
      .scaleLinear()
      .domain([
        d3.min(dataSeries.flat().map((d) => +d.x)) || 0 - 5,
        d3.max(dataSeries.flat().map((d) => +d.x)) || 0 + 5,
      ])
      .range([0, boundedWidth - offsetLegend])
      .nice();

    if (useMultipleScales) {
      const customScale = () => {
        const maxValue = d3.max(dataSeries.flat().map((d) => +d.y)) || 0;
        const maxDomain = maxValue + (maxValue * 20 / 100);
        const gap = maxDomain / 16;
        return d3
          .scaleLinear()
          .domain([0, gap, 2 * gap, 3 * gap, maxDomain])
          .range([boundedHeight, boundedHeight * 0.75, boundedHeight * 0.5, boundedHeight * 0.25, 0])
          .nice();
      };

      y = customScale();
    } else {
      y = d3.scaleLinear()
        .domain([0, d3.max(dataSeries.flat().map((d) => +d.y)) || 0])
        .range([boundedHeight + ecartMeilleurRepartition, 0])
        .nice();
    }

    line = d3
      .line()
      .x((d) => x(d.x))
      .y((d) => y(d.y))
      .defined((d) => d.y > 0);

    color = d3.scaleOrdinal().domain(seriesLabel).range(colorRange);
  };

  return {
    ...options,

    async init() {
      let csv = await d3.csv(options.url);
      const columns = csv.columns.slice(1);
      data = columns.map((label) => ({
        label,
        serie: csv.map((d) => ({
          x: d.annee,
          y: d[label],
        })),
      }));

      this.$watch("width", () => {
        this.show = false;
        this.initializeSvg(this.width, this.height, this.margin);
        scales(
          this.colorRange,
          this.offsetLegend,
          document.querySelector("[x-ref=axisY]"),
          document.querySelector("[x-ref=axisX]"),
          this.boundedHeight
        );
        this.$nextTick(() => (this.show = true));
      });
    },

    initializeSvg(width, height, margin) {
      const { boundedHeight: bh, boundedWidth: bw } = calcBoundedLimit(
        width,
        height,
        margin
      );
      this.boundedHeight = bh;
      boundedWidth = bw;
    },

    translateChart() {
      return `translate(${this.margin.left},${this.margin.top})`;
    },

    translateAxisX() {
      const self = this; // Stocke une référence à "this" pour y accéder à l'intérieur de la fonction callback de setTimeout
      return new Promise(resolve => {
        setTimeout(() => {
          const chartElement = document.querySelector('.chart');
          if (chartElement) {
            const chartWidth = chartElement.getBoundingClientRect().width - 50; // largeur de l'élément
            resolve(`translate(${chartWidth}, 0)`); // promesse avec la largeur sous forme de fonction de transformation SVG
          } else {
            console.error("Chart element not found.");
            // résoudre la promesse avec la valeur par défaut de la translation
            const defaultTranslation = `translate(0, 0)`;
            resolve(defaultTranslation);
          }
        }, 250); // Pause de 0.25 seconde
      });
    },

    translateAxisY() {
      return `translate(0, ${this.boundedHeight})`;
    },

    axisY($el, fontSize) {
      const axisGeneratorY = d3.axisLeft(y);
      axisGeneratorY.tickFormat((v) =>
        v >= 1000000
          ? v.toLocaleString("fr", {
              notation: "compact",
              compactDisplay: "short",
            })
          : v.toLocaleString("fr", { notation: "standard" })
      );
    
      if (!useMultipleScales) {
        const tickValues = y.ticks();
        axisGeneratorY.tickValues(tickValues);
    
        d3.select($el)
          .call(axisGeneratorY)
          .selectAll(".tick text")
          .attr("x", "-20")
          .attr("font-family", "New Frank")
          .attr("font-weight", "300")
          .attr("font-size", fontSize.toString());
      } else {
        const customScales = dataSeries.map((serie) => {
          const minValue = d3.min(serie, (d) => d.y);
          const maxValue = d3.max(serie, (d) => d.y);
          const maxDomain = Math.ceil(maxValue / 100000) * 100000;
    
          return {
            minValue,
            maxValue,
          };
        });
    
        const Ys = [];
        customScales.forEach((scale) => {
          const minY = scale.minValue;
          const maxY = scale.maxValue;
    
          const roundedMinY = Math.round(minY / 20000) * 20000;
          const roundedMaxY = Math.round(maxY / 20000) * 20000;
    
          if (!Ys.includes(roundedMinY)) {
            Ys.push(roundedMinY);
          }
          if (!Ys.includes(roundedMaxY)) {
            Ys.push(roundedMaxY);
          }
        });
    
        Ys.sort((a, b) => a - b);
        axisGeneratorY.tickValues(Ys);
    
        d3.select($el)
          .call(axisGeneratorY)
          .selectAll(".tick text")
          .attr("x", "-20")
          .attr("font-family", "New Frank")
          .attr("font-weight", "300")
          .attr("font-size", fontSize.toString())
          .text((d, i) => Ys[i]);
    
        // Positionnement dynamique des légendes des lignes
        // const labels = d3.selectAll(".label");
        // let previousY = 0;
        // labels.each(function (d, i) {
        //   const label = d3.select(this);
        //   const bbox = label.node().getBBox();
        //   const newY = bbox.y + bbox.height / 2;
        //   if (newY < previousY + fontSize) {
        //     // Si la nouvelle position de la légende se chevauche avec la précédente, déplace vers le bas
        //     label.attr("dy", previousY + fontSize - newY);
        //   }
        //   previousY = newY + fontSize;
        // });
      }
    },
    

    axisX($el, fontSize) {
      const axisGeneratorX = d3.axisBottom(x);
      axisGeneratorX.tickFormat((v) =>
        v.toLocaleString("fr", { notation: "standard" })
      );
      axisGeneratorX.tickValues(dataSeries[0].map((d) => d.x));
      d3.select($el)
        .call(axisGeneratorX)
        .selectAll(".tick text")
        .attr("y", "20")
        .attr("font-family", "New Frank")
        .attr("font-weight", "300")
        .attr("font-size", fontSize.toString());
    },

    get data() {
      return data;
    },

    get dataSeries() {
      return dataSeries;
    },

    get seriesLabel() {
      return seriesLabel;
    },

    get boundedWidth() {
      return boundedWidth;
    },

    get x() {
      return x;
    },

    get y() {
      return y;
    },

    get color() {
      return color;
    },

    get line() {
      return line;
    },

    get show() {
      return show;
    },

    set show(s) {
      show = s;
    },

    set useMultipleScales(value) {
      useMultipleScales = value;
    },

    set ecartMeilleurRepartition(s) {
      ecartMeilleurRepartition = s;
    },
  };
}
